import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FileDown, ArrowLeft, Package, X } from 'lucide-react';
import { fetchDashboardAlerts, fetchReceiptStats } from '../../../store/dashboardSlice';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import { ExportPopup } from './ExportPopup';

// Helper function to calculate counts similar to Dashboard
const getStatusCounts = (items = []) => ({
  completed: items.reduce(
    (acc, item) => acc + ((item.ReceiptStatus || "") === "Completed" ? 1 : 0),
    0
  ),
  partial: items.reduce(
    (acc, item) =>
      acc + ((item.ReceiptStatus || "") === "Partially Received" ? 1 : 0),
    0
  ),
  pending: items.reduce(
    (acc, item) =>
      acc + ((item.ReceiptStatus || "Pending") === "Pending" ? 1 : 0),
    0
  ),
  total: items.length,
});

const ReceiptStatsBar = ({ items, onFilterChange }) => {
  // Calculate counts using the same method as Dashboard
  const counts = getStatusCounts(items);
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
      <h3 className="text-lg justify-center font-semibold mb-3 flex items-center gap-2">
        <Package className="w-5 h-5" />
        Receipt Statistics
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
        <button 
          onClick={() => onFilterChange('partial')}
          className="flex bg-yellow-50 p-3 rounded-lg items-center flex-col hover:bg-yellow-100 transition-colors"
        >
          <p className="text-sm text-yellow-600">Partial</p>
          <p className="text-xl font-bold text-yellow-700">{counts.partial}</p>
        </button>
        <button 
          onClick={() => onFilterChange('pending')}
          className="flex flex-col items-center bg-blue-50 p-3 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <p className="text-sm text-blue-600">Pending</p>
          <p className="text-xl font-bold text-blue-700">{counts.pending}</p>
        </button>
      </div>
    </div>
  );
};

const AlertItem = ({ item }) => {
  // Calculate days left using local time zone
  const calculateDaysLeft = () => {
    if (!item.CommitedDate) return item.daysLeft || 0;
    
    try {
      // Parse the committed date
      const dueDate = new Date(item.CommitedDate);
      
      // Get current date in local time zone
      const today = new Date();
      
      // Calculate the difference in days
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (err) {
      console.error('Error calculating days left:', err);
      return item.daysLeft || 0;
    }
  };
  
  // Calculate days left using local time zone
  const daysLeft = calculateDaysLeft();
  
  return (
    <div className="p-4 bg-white rounded-lg shadow transition-all duration-300 hover:shadow-lg">
      <div className="flex justify-between items-center">
        <span className="font-medium">Indent #{item.IndentID}</span>
        <div className="flex items-center gap-2">
          <span className={`
            px-2 py-1 rounded-full text-sm font-medium
            ${item.ReceiptStatus === 'Partially Received' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}
          `}>
            {item.ReceiptStatus}
          </span>
          <span className={`text-sm ${daysLeft < 0 ? 'text-red-500' : 'text-gray-500'}`}>
            {daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days left`}
          </span>
        </div>
      </div>
      
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="font-medium">Item Name: {item.ItemName}</p>
          <p className="font-medium">Item ID: {item.ItemID}</p>
          <p>Project: {item.ProjectName}</p>
          <p>Created by: {item.Name}</p>
        </div>
        
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-50 p-2 rounded">
              <p className="text-xs text-blue-600">Requested</p>
              <p className="font-bold">{item.RequestedQuantity} {item.UnitName}</p>
            </div>
            <div className="bg-green-50 p-2 rounded">
              <p className="text-xs text-green-600">Received</p>
              <p className="font-bold">{item.QuantityReceived || 0} {item.UnitName}</p>
            </div>
            <div className="bg-orange-50 p-2 rounded">
              <p className="text-xs text-orange-600">Remaining</p>
              <p className="font-bold">{item.RemainingQuantity} {item.UnitName}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <p className="text-xs text-gray-600">Due Date</p>
            <p className="font-medium">{item.CommitedDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AlertsPage = () => {
  const { type } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  
  // Correctly destructure the loading object
  const { 
    alerts,
    receiptStats,
    loading: { alerts: alertsLoading, receipts: receiptsLoading },
    error 
  } = useSelector((state) => state.dashboard);

  // Get auth state
  const { role, id } = useSelector((state) => state.auth);

  useEffect(() => {
    if (role && id) {
      console.log('Fetching alerts with role:', role, 'id:', id);
      dispatch(fetchDashboardAlerts());
      dispatch(fetchReceiptStats());
    }
  }, [dispatch, role, id]);

  // Check if we're still loading auth data
  if (!role || !id) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4">Loading user information...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Check if we're loading alerts or receipt stats
  const isLoading = alertsLoading || receiptsLoading;
  
  const alertTypes = {
    'expired': { 
      items: alerts?.default || [], 
      title: 'Expired Items' 
    },
    'due-today': { 
      items: alerts?.danger || [], 
      title: 'Due Today' 
    },
    'due-soon': { 
      items: alerts?.alert || [], 
      title: 'Due Soon' 
    },
    'upcoming': { 
      items: alerts?.warning || [], 
      title: 'Upcoming Items' 
    }
  };

  const currentAlert = alertTypes[type];

  if (!currentAlert) {
    navigate('/dashboard');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4">Loading alerts...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => {
                dispatch(fetchDashboardAlerts());
                dispatch(fetchReceiptStats());
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Filter function to only show pending and partially received items
  const filterIncompleteItems = (items) => {
    if (!items) return [];
    return items.filter(item => 
      item.ReceiptStatus === 'Pending' || 
      item.ReceiptStatus === 'Partially Received'
    );
  };

  // Apply status filter if set
  const applyStatusFilter = (items) => {
    if (!statusFilter) return items;
    
    return items.filter(item => {
      if (statusFilter === 'partial') {
        return item.ReceiptStatus === 'Partially Received';
      } else if (statusFilter === 'pending') {
        return item.ReceiptStatus === 'Pending';
      }
      return true;
    });
  };

  const handleFilterChange = (filter) => {
    setStatusFilter(filter);
  };

  const handleClearFilter = () => {
    setStatusFilter(null);
  };

  const allItems = filterIncompleteItems(currentAlert.items);
  const filteredItems = applyStatusFilter(allItems);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      <main className="flex-grow">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/Dashboard')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <h2 className="text-xl font-bold">{currentAlert.title}</h2>
            </div>
            <button
              onClick={() => setShowExportPopup(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <FileDown className="w-4 h-4" />
              Export
            </button>
          </div>

          <ReceiptStatsBar 
            items={allItems} 
            onFilterChange={handleFilterChange} 
          />
          
          {statusFilter && (
            <div className="flex justify-end mb-4">
              <button
                onClick={handleClearFilter}
                className="flex items-center gap-2 px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                <X className="w-4 h-4" />
                Clear Filter
              </button>
            </div>
          )}
          
          <div className="space-y-4">
            {filteredItems.map((item, index) => (
              <AlertItem key={`${item.IndentID}-${index}`} item={item} />
            ))}
            {(!filteredItems || filteredItems.length === 0) && (
              <div className="text-center text-gray-500 py-8 bg-white rounded-lg shadow">
                {statusFilter 
                  ? `No ${statusFilter === 'partial' ? 'partially received' : 'pending'} items to display` 
                  : 'No pending or partially received items to display'}
              </div>
            )}
          </div>
        </div>
      </main>
      
      {showExportPopup && (
        <ExportPopup
          onClose={() => setShowExportPopup(false)}
          exportEndpoint="/api/alerts/export"
          alertData={filteredItems}
          customFilename={`alerts-${type}`}
        />
      )}
    </div>
  );
};

export default AlertsPage;